import { useRef, useState } from "react";
import { UploadSimple, VideoCamera, ImageSquare, X } from "@phosphor-icons/react";
import { uploadsApi } from "@/lib/apiClient";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

function getVideoDuration(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const vid = document.createElement("video");
        vid.preload = "metadata";
        vid.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(vid.duration); };
        vid.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read video")); };
        vid.src = url;
    });
}


export default function MediaUploader({ value, onChange, label, height = 220, accept = "both", maxDuration = null, onBusyChange = null, testId = "media-uploader" }) {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);
    const [dragging, setDragging] = useState(false);
    const inputRef = useRef();

    const displayLabel = label !== undefined ? label : t("mediaUploader.defaultLabel");
    const acceptAttr = accept === "video" ? "video/*" : accept === "image" ? "image/*" : "image/*,video/*";

    const handleFile = async (file) => {
        if (!file) return;
        const isVideo = file.type.startsWith("video/");
        const isImage = file.type.startsWith("image/");
        let durationSeconds = null;
        if (!isVideo && !isImage) return toast.error(t("mediaUploader.toasts.chooseMedia"));
        if (accept === "image" && !isImage) return toast.error(t("mediaUploader.toasts.imagesOnly"));
        if (accept === "video" && !isVideo) return toast.error(t("mediaUploader.toasts.videosOnly"));

        // Duration check BEFORE upload — zero bytes sent to backend if it fails
        if (isVideo && maxDuration !== null) {
            try {
                const duration = await getVideoDuration(file);
                durationSeconds = duration;
                if (duration > maxDuration) {
                    return toast.error(t("mediaUploader.toasts.videoTooLong", { max: maxDuration, duration: Math.round(duration) }));
                }
            } catch {
                durationSeconds = null;
            }
        }

        setBusy(true);
        onBusyChange?.(true);
        try {
            const uploaded = await uploadsApi.uploadObject(file);
            onChange?.({ ...uploaded, duration_seconds: durationSeconds, poster_url: null });
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("mediaUploader.toasts.uploadFailed"));
        } finally { setBusy(false); onBusyChange?.(false); }
    };

    const value_url = typeof value === "string" ? value : value?.url;
    const value_kind = typeof value === "string" ? "image" : value?.kind;

    return (
        <div>
            {displayLabel && <div className="overline mb-2">{displayLabel}</div>}
            <div
                className="relative rounded-2xl overflow-hidden group"
                style={{
                    background: "rgba(0,0,0,0.25)",
                    border: `1px ${dragging ? "solid" : "dashed"} ${dragging ? "var(--gold)" : "rgba(255,255,255,0.12)"}`,
                    minHeight: height,
                    transition: "border-color 0.3s ease, background 0.3s ease",
                }}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
                data-testid={testId}
            >
                <input ref={inputRef} type="file" accept={acceptAttr} className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />

                {value_url ? (
                    <>
                        {value_kind === "video" ? (
                            <video src={value_url} controls playsInline className="w-full h-full object-cover" style={{ maxHeight: height * 1.6, background: "#000" }} />
                        ) : (
                            <img src={value_url} alt="uploaded" className="w-full h-full object-cover" style={{ maxHeight: height * 1.6 }} />
                        )}
                        <button onClick={() => onChange?.(null)}
                            className="absolute top-3 right-3 w-8 h-8 rounded-full glass grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`${testId}-clear`} aria-label={t("mediaUploader.remove")}>
                            <X size={12} />
                        </button>
                    </>
                ) : (
                    <button type="button" onClick={() => inputRef.current?.click()}
                        className="w-full h-full flex flex-col items-center justify-center gap-3 py-8 text-white/60 hover:text-white transition-colors"
                        style={{ minHeight: height }}
                        data-testid={`${testId}-trigger`}
                    >
                        {busy ? (
                            <>
                                <UploadSimple size={24} className="animate-pulse" color="#D4AF37" />
                                <span className="text-sm">{t("mediaUploader.uploading")}</span>
                            </>
                        ) : (
                            <>
                                <div className="flex gap-4">
                                    {accept !== "video" && <ImageSquare size={22} color="#D4AF37" />}
                                    {accept !== "image" && <VideoCamera size={22} color="#D4AF37" />}
                                </div>
                                <span className="text-sm">
                                    {t(`mediaUploader.drop.${accept}`)}
                                </span>
                                <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">
                                    {maxDuration !== null
                                        ? t("mediaUploader.specs.videoDuration", { duration: maxDuration })
                                        : t("mediaUploader.specs.videoDefault")}
                                </span>
                            </>
                        )}
                    </button>
                )}
            </div>
        </div>
    );
}
